/**
 * Phase 87 — Page Cartes Restaurant / À l'emporter.
 *
 * Permet de :
 *  - Cocher/décocher chaque produit pour chaque carte (resto / emporter)
 *  - Définir l'ordre d'apparition (drag-free, via champ numérique)
 *  - Définir un prix carte spécifique (sinon fallback price_direct)
 *  - Activer les pictogrammes (épicé, cacahuètes, végé)
 *  - Saisir les traductions thaï (alphabet) et anglais
 *  - Générer le PDF de chaque carte en un clic
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { FileText, Save, Flame, Leaf, Loader2, Download, ImagePlus, X, Languages } from "lucide-react";

const PUBLIC_API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || "";

export default function MenuCardsAdmin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | restaurant | takeaway
  const [search, setSearch] = useState("");
  const [generating, setGenerating] = useState(null); // "restaurant" | "takeaway" | null
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({}); // {product_id: {field: value}}

  // Phase 87c — Note d'accompagnement (settings.restaurant_menu_footer)
  const [footerNote, setFooterNote] = useState("");
  const [footerDirty, setFooterDirty] = useState(false);
  const [footerSaving, setFooterSaving] = useState(false);
  // Phase 87h — Provenance viandes/poissons/crustacés (settings.menu_origins_line)
  const [originsLine, setOriginsLine] = useState("");
  const [originsDirty, setOriginsDirty] = useState(false);
  const [originsSaving, setOriginsSaving] = useState(false);

  const loadSettings = async () => {
    try {
      const r = await api.get("/settings");
      setFooterNote(r.data?.restaurant_menu_footer || "");
      setFooterDirty(false);
      setOriginsLine(r.data?.menu_origins_line || "");
      setOriginsDirty(false);
    } catch (e) { /* non bloquant */ }
  };

  const saveFooter = async () => {
    setFooterSaving(true);
    try {
      await api.put("/settings", { restaurant_menu_footer: footerNote });
      toast.success("Note de pied de carte enregistrée");
      setFooterDirty(false);
    } catch (e) {
      toast.error("Échec enregistrement");
    } finally {
      setFooterSaving(false);
    }
  };

  const saveOrigins = async () => {
    setOriginsSaving(true);
    try {
      await api.put("/settings", { menu_origins_line: originsLine });
      toast.success("Provenances enregistrées");
      setOriginsDirty(false);
    } catch (e) {
      toast.error("Échec enregistrement");
    } finally {
      setOriginsSaving(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/restaurant-menu/products?variant=all");
      setProducts(r.data || []);
      setEdits({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = products;
    if (filter === "restaurant") rows = rows.filter((p) => p.on_restaurant_menu);
    if (filter === "takeaway") rows = rows.filter((p) => p.on_takeaway_menu);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [products, filter, search]);

  // Helper pour récupérer la valeur courante (edit en cours ou produit en base)
  const get = (p, field) => {
    if (edits[p.id] && field in edits[p.id]) return edits[p.id][field];
    return p[field];
  };

  const update = (productId, field, value) => {
    setEdits((prev) => ({ ...prev, [productId]: { ...(prev[productId] || {}), [field]: value } }));
  };

  const saveAll = async () => {
    if (Object.keys(edits).length === 0) {
      toast.info("Rien à enregistrer");
      return;
    }
    setSaving(true);
    try {
      const calls = Object.entries(edits).map(([productId, patch]) =>
        api.put(`/products/${productId}`, patch)
      );
      await Promise.all(calls);
      toast.success(`${Object.keys(edits).length} produit(s) mis à jour`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const generatePdf = async (variant) => {
    setGenerating(variant);
    try {
      const url = `${PUBLIC_API_BASE}/api/restaurant-menu/pdf?variant=${variant}`;
      // On télécharge via fetch pour gérer l'auth cookie
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(JSON.parse(errText)?.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      // Phase 87ap (18/06/2026) — Compat Brave/Safari : window.open(blob:) est bloqué
      // par leurs filtres anti-tracking. On déclenche le PDF via un <a> programmé,
      // pattern qui passe les filtres et fonctionne sur Brave, Safari, Firefox, Chrome, Edge.
      const a = document.createElement("a");
      a.href = blobUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      // Pas de `download=` pour préserver l'aperçu inline dans le nouvel onglet (Firefox / Chrome).
      // Brave : ouvre quand même dans un onglet via le click anchor (contournement OK).
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Libère la mémoire après 60s (laisse le temps au navigateur de charger le PDF)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(e?.message || "Erreur génération PDF");
    } finally {
      setGenerating(null);
    }
  };

  // Phase 87b/87d — Upload imagette dédiée (compressée 800×800 / 92% côté navigateur)
  // 800px @ 92% qualité ≈ 150-200 ko, suffit pour impression A4 nette à 300 DPI
  const handleImageUpload = async (productId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Format non supporté (JPEG / PNG / WebP attendus)");
      return;
    }
    const compressed = await compressImage(file, 800, 0.92);
    try {
      await api.post(`/restaurant-menu/products/${productId}/menu-image`, { image_base64: compressed });
      toast.success("Imagette enregistrée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'upload");
    }
  };

  const handleImageDelete = async (productId) => {
    try {
      await api.post(`/restaurant-menu/products/${productId}/menu-image`, { image_base64: null });
      toast.success("Imagette retirée");
      await load();
    } catch (e) {
      toast.error("Échec suppression");
    }
  };

  // Phase 87b — Traduction batch IA (Thaï + EN) pour les plats marqués sur l'une des cartes
  const [translating, setTranslating] = useState(false);
  const translateBatch = async () => {
    const ids = products
      .filter((p) => (p.on_restaurant_menu || p.on_takeaway_menu) && (!p.name_th || !p.name_en))
      .map((p) => p.id);
    if (ids.length === 0) {
      toast.info("Tous les plats des cartes ont déjà leurs traductions (Thaï + EN).");
      return;
    }
    if (!window.confirm(
      `Traduire automatiquement ${ids.length} plat(s) en Thaï + Anglais via IA (Claude Sonnet 4.6) ?\n` +
      `Coût estimé : ~${(ids.length * 0.002).toFixed(2)} CHF. Peut prendre ~${Math.ceil(ids.length * 3)} secondes.`
    )) return;
    setTranslating(true);
    try {
      const r = await api.post("/admin/translate-products", { product_ids: ids }, { timeout: 180000 });
      const { translated_count, failed_count, skipped_count } = r.data;
      toast.success(
        `${translated_count} plat(s) traduit(s)` +
        (skipped_count ? ` · ${skipped_count} déjà à jour` : "") +
        (failed_count ? ` · ${failed_count} échec(s)` : "")
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec batch traduction");
    } finally {
      setTranslating(false);
    }
  };

  const countResto = products.filter((p) => p.on_restaurant_menu).length;
  const countEmporter = products.filter((p) => p.on_takeaway_menu).length;

  return (
    <div className="space-y-6 p-6" data-testid="menu-cards-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1C1A]">Cartes Restaurant & Emporter</h1>
          <p className="text-sm text-[#575A57] mt-1">
            Source de vérité unique pour vos cartes papier. Cochez les plats de chaque carte, ajustez prix/ordre/pictos, puis générez le PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={translateBatch}
            disabled={translating || generating !== null}
            data-testid="translate-batch-btn"
            title="Traduit automatiquement les plats marqués sur les cartes en Thaï + EN via IA Claude"
          >
            {translating
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Languages className="w-4 h-4 mr-1.5" />}
            Traduire (Thaï + EN)
          </Button>
          <Button
            variant="outline"
            onClick={() => generatePdf("restaurant")}
            disabled={generating !== null || countResto === 0}
            data-testid="generate-restaurant-pdf-btn"
          >
            {generating === "restaurant"
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <FileText className="w-4 h-4 mr-1.5" />}
            Carte resto ({countResto})
          </Button>
          <Button
            variant="outline"
            onClick={() => generatePdf("takeaway")}
            disabled={generating !== null || countEmporter === 0}
            data-testid="generate-takeaway-pdf-btn"
          >
            {generating === "takeaway"
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Download className="w-4 h-4 mr-1.5" />}
            Carte emporter ({countEmporter})
          </Button>
          <Button
            onClick={saveAll}
            disabled={saving || Object.keys(edits).length === 0}
            data-testid="save-menu-edits-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Enregistrer ({Object.keys(edits).length})
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Rechercher par nom, SKU ou catégorie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          data-testid="menu-search"
        />
        <div className="flex items-center gap-1 border rounded-md p-1">
          {[
            { key: "all", label: "Tous" },
            { key: "restaurant", label: `Resto (${countResto})` },
            { key: "takeaway", label: `Emporter (${countEmporter})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 text-xs rounded ${
                filter === f.key ? "bg-[#1F4E3D] text-white" : "text-[#575A57] hover:bg-[#F4F4F0]"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Phase 87c — Note d'accompagnement éditable (affichée en pied de chaque page de la carte resto) */}
      <Card className="bg-[#FAF6EE] border-[#E5E5E0]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-xs font-semibold text-[#575A57] uppercase tracking-wider mb-1.5">
                Note d&apos;accompagnement (pied de carte resto)
              </p>
              <textarea
                rows={2}
                value={footerNote}
                onChange={(e) => { setFooterNote(e.target.value); setFooterDirty(true); }}
                placeholder="Ex: Tous les plats sont servis avec du riz parfumé. Sur demande, en supplément : riz collant, nouilles jaunes, légumes sautés."
                className="w-full text-sm border border-[#E5E5E0] rounded p-2 bg-white focus:outline-none focus:border-[#1F4E3D] resize-none italic text-[#575A57]"
                data-testid="footer-note-textarea"
              />
              <p className="text-[10px] text-[#A3A6A3] mt-1">
                Affichée en italique gris en bas de chaque page de la carte restaurant uniquement (pas sur la carte emporter).
              </p>
            </div>
            {footerDirty && (
              <Button
                size="sm"
                onClick={saveFooter}
                disabled={footerSaving}
                data-testid="save-footer-note-btn"
              >
                {footerSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Enregistrer la note
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Phase 87h — Provenance viandes/poissons/crustacés (ODAlOUs art. 16) */}
      <Card className="bg-[#FAF6EE] border-[#E5E5E0]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-xs font-semibold text-[#575A57] uppercase tracking-wider mb-1.5">
                Provenance des viandes, poissons et crustacés (dernière page)
              </p>
              <textarea
                rows={2}
                value={originsLine}
                onChange={(e) => { setOriginsLine(e.target.value); setOriginsDirty(true); }}
                placeholder='Ex: Poulet — Suisse, UE ; Canard — Thaïlande ; Bœuf — Suisse ; Porc — Suisse ; Crustacés — Thaïlande, Vietnam ; Poisson — Thaïlande ; Calmars — Thaïlande'
                className="w-full text-sm border border-[#E5E5E0] rounded p-2 bg-white focus:outline-none focus:border-[#1F4E3D] resize-none text-[#1F2624]"
                data-testid="origins-line-textarea"
              />
              <p className="text-[10px] text-[#A3A6A3] mt-1">
                Affichée sur une page dédiée à la fin de la carte. Séparez chaque élément par <code className="bg-[#FAF6EE] px-1">;</code>. Laissez vide pour ne pas afficher cette page.
              </p>
            </div>
            {originsDirty && (
              <Button
                size="sm"
                onClick={saveOrigins}
                disabled={originsSaving}
                data-testid="save-origins-line-btn"
              >
                {originsSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Enregistrer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-[#575A57]">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">SKU</TableHead>
                  <TableHead className="w-20 text-center">Photo</TableHead>
                  <TableHead>Plat (FR + Thaï + EN)</TableHead>
                  <TableHead className="w-20 text-center">Resto</TableHead>
                  <TableHead className="w-20 text-center">Emp.</TableHead>
                  <TableHead className="w-20 text-right">Prix</TableHead>
                  <TableHead className="w-16 text-center">Ordre</TableHead>
                  <TableHead className="w-32 text-center">Section</TableHead>
                  <TableHead className="w-32 text-center">Pictos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`menu-row-${p.sku}`}>
                    <TableCell className="font-mono text-xs text-[#575A57]">{p.sku}</TableCell>
                    <TableCell>
                      <MenuImageCell
                        productId={p.id}
                        sku={p.sku}
                        imageUrl={p.menu_image_url}
                        onUpload={(file) => handleImageUpload(p.id, file)}
                        onDelete={() => handleImageDelete(p.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium text-[#1A1C1A]">{p.name}</div>
                        <Input
                          placeholder="Nom court resto (sans accompagnement, fallback nom complet)"
                          value={get(p, "name_restaurant") || ""}
                          onChange={(e) => update(p.id, "name_restaurant", e.target.value)}
                          className="h-7 text-sm"
                          data-testid={`name-resto-${p.sku}`}
                        />
                        <Input
                          placeholder="Nom thaï (alphabet) — ex: ผัดไทยไก่"
                          value={get(p, "name_th") || ""}
                          onChange={(e) => update(p.id, "name_th", e.target.value)}
                          className="h-7 text-sm"
                          data-testid={`name-th-${p.sku}`}
                        />
                        <Input
                          placeholder="Nom anglais — ex: Pad Thai chicken"
                          value={get(p, "name_en") || ""}
                          onChange={(e) => update(p.id, "name_en", e.target.value)}
                          className="h-7 text-sm italic"
                          data-testid={`name-en-${p.sku}`}
                        />
                        <Input
                          placeholder="Accompagnement imposé (ex: riz collant pour les laab)"
                          value={get(p, "mandatory_side") || ""}
                          onChange={(e) => update(p.id, "mandatory_side", e.target.value)}
                          className="h-7 text-xs italic text-[#8C6B2F]"
                          data-testid={`mandatory-side-${p.sku}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={Boolean(get(p, "on_restaurant_menu"))}
                        onCheckedChange={(v) => update(p.id, "on_restaurant_menu", v)}
                        data-testid={`on-resto-${p.sku}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={Boolean(get(p, "on_takeaway_menu"))}
                        onCheckedChange={(v) => update(p.id, "on_takeaway_menu", v)}
                        data-testid={`on-takeaway-${p.sku}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.5"
                        placeholder={p.price_direct != null ? String(p.price_direct) : "—"}
                        value={get(p, "menu_price") ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          update(p.id, "menu_price", v === "" ? null : parseFloat(v));
                        }}
                        className="h-7 w-20 text-right text-sm"
                        data-testid={`menu-price-${p.sku}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        value={get(p, "menu_order") ?? 0}
                        onChange={(e) => update(p.id, "menu_order", parseInt(e.target.value, 10) || 0)}
                        className="h-7 w-14 text-center text-sm"
                        data-testid={`menu-order-${p.sku}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <select
                        value={get(p, "menu_section") || ""}
                        onChange={(e) => update(p.id, "menu_section", e.target.value)}
                        className="h-7 text-sm border border-[#E5E5E0] rounded px-1.5 bg-white focus:outline-none focus:border-[#1F4E3D]"
                        data-testid={`menu-section-${p.sku}`}
                      >
                        <option value="">— Non classé —</option>
                        <option value="starters">Entrées</option>
                        <option value="mains">Plats de viande</option>
                        <option value="vege">Plats végétariens</option>
                        <option value="divers">Divers (fondues…)</option>
                        <option value="kids">Pour enfants</option>
                        <option value="desserts">Desserts</option>
                      </select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-center">
                        <SpiceLevelPicker
                          level={Number(get(p, "menu_spice_level") || 0)}
                          onChange={(lvl) => update(p.id, "menu_spice_level", lvl)}
                          testId={`spice-level-${p.sku}`}
                        />
                        <PictoToggle
                          icon={<Leaf className="w-4 h-4" />}
                          color="#5A8A4E"
                          title="Végétarien"
                          active={Boolean(get(p, "menu_picto_vegetarian"))}
                          onToggle={(v) => update(p.id, "menu_picto_vegetarian", v)}
                          testId={`picto-vege-${p.sku}`}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PictoToggle({ icon, color, title, active, onToggle, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={() => onToggle(!active)}
      className="p-1.5 rounded transition-colors"
      style={{
        backgroundColor: active ? `${color}15` : "transparent",
        color: active ? color : "#D6D2C7",
        border: `1px solid ${active ? color : "transparent"}`,
      }}
      data-testid={testId}
    >
      {icon}
    </button>
  );
}

// Phase 87g — Sélecteur de force "piment" (0 = pas affiché, 1 = légèrement épicé,
// 2 = bien épicé, 3 = très épicé). Cliquer sur un piment ↔ niveau ; reclic sur le
// même niveau = remise à 0.
function SpiceLevelPicker({ level, onChange, testId }) {
  const SPICE_LABELS = ["pas épicé", "légèrement épicé", "bien épicé", "très épicé"];
  const setLevel = (n) => onChange(level === n ? 0 : n);
  return (
    <div
      className="flex items-center gap-0.5 px-1.5 py-1 rounded border border-transparent hover:border-[#D6D2C7]"
      title={SPICE_LABELS[level] || ""}
      data-testid={testId}
    >
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => setLevel(n)}
          className="transition-transform hover:scale-110"
          title={SPICE_LABELS[n]}
          data-testid={`${testId}-${n}`}
        >
          <Flame
            className="w-4 h-4"
            style={{
              color: level >= n ? "#C53030" : "#E5E2D8",
              fill: level >= n ? "#C53030" : "none",
            }}
          />
        </button>
      ))}
    </div>
  );
}

// Cellule "Photo" : preview ronde + bouton upload + bouton supprimer
function MenuImageCell({ productId, sku, imageUrl, onUpload, onDelete }) {
  const inputId = `menu-img-${productId}`;
  return (
    <div className="flex flex-col items-center gap-1">
      {imageUrl ? (
        <div className="relative group">
          <img
            src={imageUrl}
            alt={sku}
            className="w-12 h-12 object-cover rounded border border-[#E5E5E0]"
            data-testid={`menu-image-preview-${sku}`}
          />
          <button
            type="button"
            onClick={onDelete}
            className="absolute -top-1 -right-1 bg-[#C05C47] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
            title="Retirer l'imagette"
            data-testid={`menu-image-delete-${sku}`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="w-12 h-12 border-2 border-dashed border-[#D6D2C7] rounded flex items-center justify-center cursor-pointer hover:border-[#1F4E3D] hover:bg-[#F4F4F0] transition-colors"
          title="Ajouter une imagette pour la carte"
        >
          <ImagePlus className="w-4 h-4 text-[#A3A6A3]" />
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = ""; // permet de réuploader la même image
        }}
        data-testid={`menu-image-upload-${sku}`}
      />
    </div>
  );
}

// Compresse une image côté navigateur : max 400×400, JPEG qualité 82%.
// Retourne une data URL base64 (~30-60 ko par image au lieu de plusieurs Mo bruts).
function compressImage(file, maxSize = 400, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
